import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = 'https://coast-terminal-production.up.railway.app';

// ── Design tokens ─────────────────────────────────────────────────────────────
const bg        = '#ffffff';
const surface   = '#fafaf8';
const border    = '#d4c8b4';
const gold      = '#b8956a';
const text      = '#0d3d5c';
const textSec   = '#12526e';
const textMut   = '#4a6070';
const green     = '#1A7A4A';
const red       = '#CC2200';

const fHead = "'Fraunces', Georgia, serif";
const fBody = "'EB Garamond', Georgia, serif";

// ── Types ─────────────────────────────────────────────────────────────────────

interface QualifiedFirm {
  cik: string;
  name: string;
  website_url: string | null;
  state: string | null;
  property_focus: string | null;
  active_news_count: number | null;
  strategy_brief: string | null;
  investment_philosophy: string | null;
  philosophy_synthetic?: boolean;
  key_people: string[] | null;
  founded_year: number | null;
  blog_url: string | null;
  aum_usd: number | null;
  richness?: number;
  iapd_reg_year?: number | null;
  deal_count?: number;
  article_count?: number;
  last_deal_at?: string | null;
}

interface DealAnalysis {
  why: string | null;
  impact: string | null;
  prediction: string | null;
}

interface Deal {
  id: number;
  title: string;
  url: string | null;
  source: string | null;
  published_at: string | null;
  summary: string | null;
  relevance: string | null;
  deal_size_usd: number | null;
  property_type: string | null;
  market: string | null;
  analysis: DealAnalysis | null;
}

interface SectorPeer {
  cik: string;
  name: string;
  aum_usd: number | null;
  deal_count: number;
  raise_count: number;
  state: string | null;
  founded_year: number | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt$(n: number | null | undefined): string {
  if (!n) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

function fmtDate(s: string | null): string {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return s; }
}

const FOCUS_LABELS: Record<string, string> = {
  multifamily: 'Multifamily', industrial: 'Industrial', office: 'Office',
  retail: 'Retail', hotel: 'Hotel', residential: 'Residential',
  commercial: 'Commercial', diversified: 'Diversified', other: 'Other',
};

// ── Firm Card ─────────────────────────────────────────────────────────────────

function FirmCard({ firm, onClick }: { firm: QualifiedFirm; onClick: () => void }) {
  const [hov, setHov] = useState(false);
  const thisYear = new Date().getFullYear();
  const age = firm.founded_year ? thisYear - firm.founded_year : null;
  const people = firm.key_people?.slice(0, 3) || [];
  const isSparse = (firm.richness ?? 0) <= 1;       // no real enrichment yet

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: surface,
        border: `1px solid ${hov ? gold + '66' : border}`,
        borderTop: `3px solid ${hov ? gold : border}`,
        padding: '28px 30px',
        cursor: 'pointer',
        transition: 'border-color 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      {/* Top row: focus tag + deal count + enrichment hint */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 10 }}>
        <span style={{
          color: gold, fontSize: 9, fontFamily: fBody,
          letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700,
        }}>
          {firm.property_focus ? (FOCUS_LABELS[firm.property_focus] || firm.property_focus) : 'Real Estate'}
        </span>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {(firm.deal_count ?? 0) > 0 && (
            <span style={{
              color: green, fontFamily: fHead, fontSize: 13, fontWeight: 800,
              letterSpacing: '0.02em',
            }}>
              {firm.deal_count} {firm.deal_count === 1 ? 'deal' : 'deals'}
            </span>
          )}
          {(firm.article_count ?? 0) > (firm.deal_count ?? 0) && (
            <span style={{ color: textMut, fontSize: 11, fontFamily: fBody }}>
              · {firm.article_count} articles
            </span>
          )}
          {isSparse && (
            <span title="Profile pending enrichment — basic IAPD data only" style={{
              color: textMut, fontSize: 9, fontFamily: fBody,
              letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600,
              border: `1px solid ${border}`, padding: '2px 6px',
            }}>
              IAPD basics
            </span>
          )}
        </div>
      </div>

      {/* Firm name */}
      <h3 style={{
        fontFamily: fHead, color: hov ? gold : text,
        fontSize: 20, fontWeight: 700, margin: '0 0 6px 0',
        lineHeight: 1.25, transition: 'color 0.15s',
      }}>
        {firm.name}
      </h3>

      {/* State · Est. year */}
      <div style={{ color: textMut, fontSize: 12, fontFamily: fBody, marginBottom: 16 }}>
        {[firm.state, firm.founded_year ? `Est. ${firm.founded_year}${age ? ` · ${age} yrs` : ''}` : null]
          .filter(Boolean).join(' · ')}
      </div>

      {/* Cards stay fact-only: state · est year · sector focus · AUM ·
          deal/article counts. We previously rendered investment_philosophy
          and key_people, both of which were AI-generated and therefore
          excluded under the factual-only rule. */}

      {/* Bottom row: AUM + arrow */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 16, borderTop: `1px solid ${border}` }}>
        {firm.aum_usd ? (
          <span style={{ color: gold, fontFamily: fHead, fontSize: 15, fontWeight: 600 }}>
            {fmt$(firm.aum_usd)} AUM
          </span>
        ) : <span />}
        <span style={{ color: hov ? gold : textMut, fontSize: 12, fontFamily: fBody, transition: 'color 0.15s' }}>
          View firm →
        </span>
      </div>
    </div>
  );
}

// ── Deal Row ──────────────────────────────────────────────────────────────────

function DealRow({ deal }: { deal: Deal }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ borderBottom: `1px solid ${border}`, paddingBottom: 20, marginBottom: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          {deal.url ? (
            <a
              href={deal.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: fHead, color: text, fontSize: 16, fontWeight: 700, textDecoration: 'none', lineHeight: 1.3 }}
            >
              {deal.title}
            </a>
          ) : (
            <div style={{ fontFamily: fHead, color: text, fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>
              {deal.title}
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, marginTop: 5, flexWrap: 'wrap' }}>
            {deal.source && (
              <span style={{ color: gold, fontSize: 10, fontFamily: fBody, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {deal.source}
              </span>
            )}
            {deal.published_at && (
              <span style={{ color: textMut, fontSize: 11, fontFamily: fBody }}>{fmtDate(deal.published_at)}</span>
            )}
            {deal.deal_size_usd && (
              <span style={{ color: green, fontSize: 11, fontFamily: fBody, fontWeight: 600 }}>{fmt$(deal.deal_size_usd)}</span>
            )}
            {deal.market && (
              <span style={{ color: textMut, fontSize: 11, fontFamily: fBody }}>{deal.market}</span>
            )}
            {deal.property_type && (
              <span style={{ color: textMut, fontSize: 11, fontFamily: fBody }}>{deal.property_type}</span>
            )}
          </div>
        </div>
      </div>

      {/* Summary — primary-source content from the article itself */}
      {deal.summary && (
        <p style={{ fontFamily: fBody, color: textSec, fontSize: 13, lineHeight: 1.7, margin: '0 0 10px 0' }}>
          {deal.summary}
        </p>
      )}
    </div>
  );
}

// ── Sector Peers Chart ────────────────────────────────────────────────────────

function SectorPeersPanel({
  peers, focusLabel, thisFirm,
}: {
  peers: SectorPeer[];
  focusLabel: string;
  thisFirm: QualifiedFirm;
}) {
  if (peers.length === 0) return null;

  // Sort by deal count desc for the bar chart
  const sorted = [...peers].sort((a, b) => b.deal_count - a.deal_count);
  const maxDeals = Math.max(...sorted.map(p => p.deal_count), 1);

  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{ borderTop: `1px solid ${border}`, paddingTop: 28, marginBottom: 20 }}>
        <div style={{ color: textMut, fontSize: 9, fontFamily: fBody, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
          Sector Comparison
        </div>
        <div style={{ color: textSec, fontSize: 12, fontFamily: fBody }}>
          {focusLabel} peers by deal activity
        </div>
      </div>

      {/* Bar chart */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sorted.map((peer) => {
          const isThis = peer.cik === thisFirm.cik;
          const barPct = (peer.deal_count / maxDeals) * 100;
          return (
            <div key={peer.cik} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 180, fontFamily: fBody, fontSize: 12,
                color: isThis ? gold : text,
                fontWeight: isThis ? 700 : 400,
                flexShrink: 0, textAlign: 'right',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {peer.name}
              </div>
              <div style={{ flex: 1, background: '#eae5dc', height: 10, position: 'relative' }}>
                <div style={{
                  position: 'absolute', left: 0, top: 0, height: '100%',
                  width: `${barPct}%`,
                  background: isThis ? gold : border,
                  transition: 'width 0.4s ease',
                }} />
              </div>
              <div style={{ width: 60, fontFamily: fBody, fontSize: 11, color: isThis ? gold : textMut, flexShrink: 0 }}>
                {peer.deal_count} deals
                {peer.raise_count > 0 && ` · ${peer.raise_count} raises`}
              </div>
              {peer.aum_usd && (
                <div style={{ width: 70, fontFamily: fBody, fontSize: 11, color: textMut, flexShrink: 0, textAlign: 'right' }}>
                  {fmt$(peer.aum_usd)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Firm Detail ───────────────────────────────────────────────────────────────

function FirmDetail({
  firm, onBack, token,
}: {
  firm: QualifiedFirm;
  onBack: () => void;
  token: string;
}) {
  const auth = { Authorization: `Bearer ${token}` };
  const [deals, setDeals] = useState<Deal[]>([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [counts, setCounts] = useState<{deal: number; fundraise: number; news: number; total: number}>(
    { deal: 0, fundraise: 0, news: 0, total: 0 }
  );
  const [filter, setFilter] = useState<'all' | 'deal' | 'fundraise' | 'news'>('all');
  const [peers, setPeers] = useState<SectorPeer[]>([]);
  const [peersLoading, setPeersLoading] = useState(true);

  useEffect(() => {
    setDealsLoading(true);
    axios
      .get(`${API}/peers/firms/${firm.cik}/deals?limit=200`, { headers: auth })
      .then(r => {
        setDeals(r.data.deals || []);
        setCounts(r.data.counts || { deal: 0, fundraise: 0, news: 0, total: 0 });
      })
      .catch(console.error)
      .finally(() => setDealsLoading(false));

    setPeersLoading(true);
    axios
      .get(`${API}/peers/firms/${firm.cik}/sector-peers`, { headers: auth })
      .then(r => setPeers(r.data.peers || []))
      .catch(console.error)
      .finally(() => setPeersLoading(false));
  }, [firm.cik]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleDeals = filter === 'all' ? deals : deals.filter(d => d.relevance === filter);

  const focusLabel = firm.property_focus
    ? (FOCUS_LABELS[firm.property_focus] || firm.property_focus)
    : 'Firm Profile';

  // Legacy buckets — kept so the existing render block doesn't crash, but
  // we now use `visibleDeals` (filter-aware) for the actual list.
  const dealArticles = visibleDeals.filter(d => d.relevance === 'deal');
  const otherArticles = visibleDeals.filter(d => d.relevance !== 'deal');

  return (
    <div style={{ maxWidth: 900, padding: '0 52px' }}>
      <button
        onClick={onBack}
        style={{ background: 'none', border: 'none', color: textSec, fontSize: 13, fontFamily: fBody, cursor: 'pointer', padding: '0 0 32px 0', display: 'block' }}
      >
        ← Back to Firms
      </button>

      {/* Masthead */}
      <div style={{ borderTop: `3px solid ${text}`, paddingTop: 20, marginBottom: 4 }}>
        <div style={{ color: gold, fontSize: 10, fontFamily: fBody, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>
          {focusLabel}
        </div>
        <h1 style={{ fontFamily: fHead, color: text, fontSize: 42, fontWeight: 900, margin: 0, letterSpacing: '-0.02em' }}>
          {firm.name}
        </h1>
        <div style={{ borderBottom: `1px solid ${text}`, marginTop: 14 }} />
        <div style={{ borderBottom: `3px solid ${text}`, marginTop: 3, marginBottom: 32 }} />
      </div>

      {/* Meta strip */}
      <div style={{ display: 'flex', gap: 40, marginBottom: 36, flexWrap: 'wrap' }}>
        {firm.founded_year && (
          <div>
            <div style={{ color: textMut, fontSize: 9, fontFamily: fBody, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>Founded</div>
            <div style={{ color: text, fontSize: 16, fontFamily: fBody }}>{firm.founded_year}</div>
          </div>
        )}
        {firm.state && (
          <div>
            <div style={{ color: textMut, fontSize: 9, fontFamily: fBody, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>HQ State</div>
            <div style={{ color: text, fontSize: 16, fontFamily: fBody }}>{firm.state}</div>
          </div>
        )}
        {firm.aum_usd && (
          <div>
            <div style={{ color: textMut, fontSize: 9, fontFamily: fBody, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>AUM</div>
            <div style={{ color: gold, fontSize: 16, fontFamily: fHead, fontWeight: 600 }}>{fmt$(firm.aum_usd)}</div>
          </div>
        )}
        {firm.website_url && (
          <div>
            <div style={{ color: textMut, fontSize: 9, fontFamily: fBody, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>Website</div>
            <a href={firm.website_url} target="_blank" rel="noopener noreferrer" style={{ color: gold, fontSize: 14, fontFamily: fBody, textDecoration: 'none' }}>
              {firm.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')} ↗
            </a>
          </div>
        )}
        {firm.active_news_count != null && firm.active_news_count > 0 && (
          <div>
            <div style={{ color: textMut, fontSize: 9, fontFamily: fBody, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>Press Coverage</div>
            <div style={{ color: green, fontSize: 16, fontFamily: fBody, fontWeight: 600 }}>{firm.active_news_count} articles</div>
          </div>
        )}
      </div>

      {/* Investment philosophy / key_people / strategy_brief intentionally
          omitted — those fields were AI-generated from website scrapes and
          violate the factual-only rule. The detail surface stays IAPD facts
          + the news-article feed below. */}

      {/* Sector comparison */}
      {!peersLoading && peers.length > 0 && (
        <SectorPeersPanel peers={peers} focusLabel={focusLabel} thisFirm={firm} />
      )}

      {/* Deal history */}
      <div style={{ borderTop: `1px solid ${border}`, paddingTop: 28, marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 6, flexWrap: 'wrap' }}>
          <div>
            <div style={{ color: textMut, fontSize: 9, fontFamily: fBody, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
              Deal History
            </div>
            <div style={{ color: text, fontFamily: fHead, fontSize: 22, fontWeight: 700 }}>
              {counts.total} {counts.total === 1 ? 'event' : 'events'} tracked
            </div>
          </div>
          {counts.total > 0 && (
            <div style={{ display: 'flex', gap: 14, fontFamily: fBody, fontSize: 13, color: textMut }}>
              {counts.deal > 0      && <span><strong style={{ color: green }}>{counts.deal}</strong> {counts.deal === 1 ? 'deal' : 'deals'}</span>}
              {counts.fundraise > 0 && <span><strong style={{ color: gold }}>{counts.fundraise}</strong> {counts.fundraise === 1 ? 'raise' : 'raises'}</span>}
              {counts.news > 0      && <span><strong style={{ color: textSec }}>{counts.news}</strong> news</span>}
            </div>
          )}
        </div>
        <div style={{ color: textSec, fontSize: 12, fontFamily: fBody, marginBottom: 16 }}>
          Acquisitions, dispositions, fundraises, and press coverage indexed from public sources.
          Click an item for details. Items with{' '}
          <span style={{ color: gold, fontWeight: 600 }}>AI Analysis</span> include a written
          why / impact / outlook breakdown.
        </div>

        {/* Filter pills */}
        {counts.total > 0 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap' }}>
            {(
              [
                ['all',       `All (${counts.total})`],
                ['deal',      `Deals (${counts.deal})`],
                ['fundraise', `Raises (${counts.fundraise})`],
                ['news',      `News (${counts.news})`],
              ] as [typeof filter, string][]
            ).map(([key, label]) => {
              const active = filter === key;
              const dim = key !== 'all' && (counts as any)[key] === 0;
              return (
                <button
                  key={key}
                  disabled={dim}
                  onClick={() => setFilter(key)}
                  style={{
                    background: active ? gold : 'transparent',
                    color:      active ? '#fff' : (dim ? textMut : textSec),
                    border: `1px solid ${active ? gold : border}`,
                    padding: '5px 12px',
                    fontFamily: fBody, fontSize: 11, fontWeight: 600,
                    letterSpacing: '0.05em', textTransform: 'uppercase',
                    cursor: dim ? 'not-allowed' : 'pointer',
                    opacity: dim ? 0.45 : 1,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {dealsLoading ? (
          <div style={{ color: textMut, fontFamily: fBody, fontSize: 14, padding: '32px 0' }}>
            Loading deal history…
          </div>
        ) : dealArticles.length === 0 && otherArticles.length === 0 ? (
          <div style={{ color: textMut, fontFamily: fBody, fontSize: 13, fontStyle: 'italic', padding: '24px 0' }}>
            No deal history indexed yet. Deal articles are collected during the nightly enrichment cycle (2 AM ET).
          </div>
        ) : (
          <>
            {dealArticles.length > 0 && (
              <div style={{ marginBottom: 32 }}>
                {dealArticles.map(d => <DealRow key={d.id} deal={d} />)}
              </div>
            )}

            {otherArticles.length > 0 && (
              <>
                {dealArticles.length > 0 && (
                  <div style={{ color: textMut, fontSize: 9, fontFamily: fBody, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16, marginTop: 8 }}>
                    Other Coverage
                  </div>
                )}
                {otherArticles.map(d => <DealRow key={d.id} deal={d} />)}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface FirmsProps {
  token: string;
}

export default function Firms({ token }: FirmsProps) {
  const auth = { Authorization: `Bearer ${token}` };

  const [view,         setView]         = useState<'grid' | 'detail'>('grid');
  const [selectedFirm, setSelectedFirm] = useState<QualifiedFirm | null>(null);
  const [firms,        setFirms]        = useState<QualifiedFirm[]>([]);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [q,            setQ]            = useState('');

  useEffect(() => {
    setLoading(true);
    const params: Record<string, string | number> = { limit: 500, offset: 0 };
    if (q) params.q = q;
    axios
      .get(`${API}/peers/firms/qualified`, { headers: auth, params })
      .then(r => {
        setFirms(r.data.firms || []);
        setTotal(r.data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (firm: QualifiedFirm) => {
    setSelectedFirm(firm);
    setView('detail');
  };

  const handleBack = () => {
    setView('grid');
    setSelectedFirm(null);
  };

  return (
    <div style={{ background: bg, minHeight: '100vh', padding: '36px 52px' }}>

      {view === 'detail' && selectedFirm ? (
        <FirmDetail firm={selectedFirm} onBack={handleBack} token={token} />
      ) : (
        <>
          {/* ── Masthead ── */}
          <div style={{ borderTop: `3px solid ${text}`, paddingTop: 16, marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
              <h1 style={{
                fontFamily: fHead, color: text,
                fontSize: 52, fontWeight: 900, margin: 0,
                letterSpacing: '-0.03em', lineHeight: 1,
              }}>
                Firms
              </h1>
              <div style={{ paddingBottom: 6, color: textMut, fontSize: 12, fontFamily: fBody }}>
                {total > 0 ? `${total} qualified firms` : ''}
              </div>
            </div>
            <div style={{ borderBottom: `1px solid ${text}`, marginTop: 12 }} />
            <div style={{ borderBottom: `3px solid ${text}`, marginTop: 3, marginBottom: 28 }} />
          </div>

          {/* ── Search bar ── */}
          <div style={{ marginBottom: 28 }}>
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search firms…"
              style={{
                width: 320, fontFamily: fBody, fontSize: 14,
                border: `1px solid ${border}`, borderBottom: `2px solid ${text}`,
                padding: '9px 14px', color: text, background: bg,
                outline: 'none',
              }}
            />
          </div>

          {/* ── Content ── */}
          {loading ? (
            <div style={{ color: textSec, fontFamily: fBody, fontSize: 15, padding: '72px 0', textAlign: 'center' }}>
              Loading firms…
            </div>
          ) : firms.length === 0 ? (
            <div style={{ padding: '64px 0', textAlign: 'center', borderTop: `1px solid ${border}` }}>
              <p style={{ color: textSec, fontFamily: fBody, fontSize: 16, margin: '0 0 10px' }}>
                No qualified firms yet.
              </p>
              <p style={{ color: textMut, fontFamily: fBody, fontSize: 13, margin: 0 }}>
                Firms appear here once enrichment confirms: website, blog/newsletter, key people, founding date, and investment philosophy.
                <br />The nightly enrichment cycle (2 AM ET) populates these fields automatically.
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: 20,
            }}>
              {firms.map(f => (
                <FirmCard key={f.cik} firm={f} onClick={() => handleSelect(f)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
